import { Typography } from 'antd';
import { TextProps } from 'antd/es/typography/Text';
import * as React from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';

type WithTextChangeProps = {
  label: string;
  Wrapper: React.FC<TextProps>;
  transitionTime: number;
  className?: string;
};

const transitionStyles: Record<
  Exclude<TransitionStatus, 'unmounted'>,
  { opacity: number }
> = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

const WithTextChange: React.FC<WithTextChangeProps> = ({
  label,
  Wrapper = Typography.Text,
  transitionTime,
  className,
}) => {
  const [visible, setVisible] = React.useState<boolean>(false);

  const ref = React.useRef<HTMLSpanElement | null>(null);

  React.useEffect(() => {
    setVisible(false);
  }, [label]);

  React.useEffect(() => {
    setVisible(true);
  }, []);

  const handleShow = React.useCallback(() => {
    setVisible(true);
  }, []);

  const defaultStyle = React.useMemo(() => {
    return {
      transition: `opacity ${transitionTime}ms ease-in-out`,
      opacity: 0,
    };
  }, [transitionTime]);

  return (
    <Transition
      in={visible}
      onExited={handleShow}
      timeout={{
        appear: transitionTime / 2,
        enter: transitionTime / 2,
        exit: 0,
      }}
      nodeRef={ref}
    >
      {(status) => (
        <Wrapper
          className={className}
          style={{
            ...defaultStyle,
            ...transitionStyles[status],
          }}
          ref={ref}
        >
          {label}
        </Wrapper>
      )}
    </Transition>
  );
};

export default WithTextChange;
